import React, { createContext, useState, useEffect, useContext } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as api from '../api/auth';
import * as lmsService from '../services/lmsService';
import { onUnauthorized } from '../services/api';

export const AuthContext = createContext({});
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkAuth();
    // Register global 401 handler
    onUnauthorized(() => {
      console.log('AuthContext: Unauthorized 401 received, clearing state');
      setUser(null);
    });
  }, []);

  const checkAuth = async () => {
    try {
      console.log('Checking authentication state...');
      const token = await AsyncStorage.getItem('token');
      const userData = await AsyncStorage.getItem('user');

      console.log('Stored token:', token ? 'Present' : 'Missing');
      console.log('Stored user data:', userData ? 'Present' : 'Missing');

      if (token && userData) {
        const parsedUser = JSON.parse(userData);
        console.log('Validating session for user:', parsedUser.id);
        
        try {
          // Verify token with server
          const response = await api.verifyToken();
          if (response && response.user) {
            console.log('Session verified successfully');
            
            // Handle rolling session: update token if server returned a fresh one
            const newToken = response.token || token;
            if (response.token && response.token !== token) {
              console.log('Updating session with fresh token');
              await AsyncStorage.setItem('token', response.token);
            }

            setUser({ ...response.user, token: newToken });
          } else {
            throw new Error('Invalid session response');
          }
        } catch (verifyErr) {
          console.warn('Session verification failed on startup:', verifyErr.message);
          // If it's a 401, the interceptor might have already cleared storage,
          // but we ensure state is cleared here.
          await AsyncStorage.multiRemove(['token', 'user']);
          setUser(null);
        }
      } else {
        console.log('No stored authentication found');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    setError(null);
    try {
      console.log('Attempting login...', { email });

      // Attempt login
      const response = await api.loginUser(email, password);
      console.log('Login response received');

      // Verify token and user data are stored
      const [storedToken, storedUser] = await Promise.all([
        AsyncStorage.getItem('token'),
        AsyncStorage.getItem('user')
      ]);

      if (!storedToken || !storedUser) {
        throw new Error('Failed to store authentication data');
      }

      console.log('Auth data stored successfully');
      const userData = response.user;

      // Log participant data if available
      if (userData.participant) {
        console.log('Participant data:', {
          number: userData.participant.participantNumber,
          fcp: userData.participant.fcp?.name,
          cluster: userData.participant.fcp?.cluster?.name,
        });
      }

      setUser({ ...userData, token: response.token });
      return response;
    } catch (error) {
      console.error('Login error:', error);

      // Clear any partial auth data on error
      await AsyncStorage.multiRemove(['token', 'user']);

      let errorMessage = 'An unexpected error occurred. Please try again.';

      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.message === 'Network Error') {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
      } else if (error.response?.status === 401) {
        errorMessage = 'Invalid email or password';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const [isNewRegistration, setIsNewRegistration] = useState(false);

  const register = async (userData) => {
    setError(null);
    try {
      console.log('Registering user with participant number:', userData.participantNumber);
      const response = await api.registerUser(userData);
      console.log('Registration successful, participant data:', response.user.participant);
      setIsNewRegistration(true);
      setUser(response.user);
      return response;
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = (typeof error === 'string') ? error : (error.message || 'Registration failed');
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const clearNewRegistration = () => setIsNewRegistration(false);

  const getParticipantNumber = () => {
    return user?.participant?.participantNumber || null;
  };

  const getParticipantInfo = () => {
    if (!user?.participant) return null;
    return {
      participantNumber: user.participant.participantNumber,
      fcp: user.participant.fcp?.name,
      cluster: user.participant.fcp?.cluster?.name
    };
  };

  const logout = async () => {
    setError(null);
    try {
      console.log('AuthContext: Starting logout');
      await api.logoutUser();
      await lmsService.clearLMSCache();
      console.log('AuthContext: AsyncStorage cleared');
      setUser(null);
      console.log('AuthContext: User state cleared');
      return { success: true };
    } catch (error) {
      console.error('AuthContext: Logout error:', error);
      setError(error.message || 'Logout failed');
      throw error;
    }
  };

  const updateProfile = async (userData, profileImage = null) => {
    setError(null);
    try {
      const response = await api.updateProfile(userData, profileImage);
      setUser(response.user);
      return response;
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.message || 'Profile update failed');
      throw error;
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setError(null);
    try {
      const response = await api.changePassword(currentPassword, newPassword);
      return response;
    } catch (error) {
      console.error('Password change error:', error);
      setError(error.message || 'Password change failed');
      throw error;
    }
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        login,
        register,
        logout,
        updateUser: updateProfile, // alias updateProfile as updateUser
        changePassword,
        clearError,
        isAuthenticated: !!user,
        participantNumber: getParticipantNumber,
        participantInfo: getParticipantInfo,
        isNewRegistration,
        clearNewRegistration
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
